import { Button, Flex, Text } from '@chakra-ui/react';

import { Modal, useToast } from '@morressier/alembic';

import { useMutation } from '@tanstack/react-query';

import { notifyAuthors } from 'src/api/mutations';

import { useSubmissionId } from 'src/hooks/useSubmissionId';

interface NotifyAuthorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotifyAuthorConfirmationModal({
  isOpen,
  onClose
}: NotifyAuthorModalProps) {
  const submissionId = useSubmissionId();
  const toast = useToast();

  const notifyMutation = useMutation({
    mutationFn: notifyAuthors,
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Notified author',
        duration: 3000,
        isClosable: true
      });
      onClose();
    },

    onError: () => {
      toast({
        variant: 'error',
        title: 'Failed to notify author',
        duration: 5000,
        isClosable: true
      });
    }
  });

  const onConfirm = () => {
    if (!submissionId) return;

    notifyMutation.mutate(submissionId);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="755px"
      closeButtonProps={{ hidden: true }}
    >
      <Flex
        padding={8}
        gap={6}
        bgColor="surfacePrimaryFill"
        flexDirection="column"
        borderRadius="base"
      >
        <Text textStyle="headingTitle">Notify Author?</Text>

        <Text textStyle="textBody">
          Are you sure you want to notify the author? An email will be sent with
          the latest submission updates. Please ensure all required changes have
          been completed before sending the notification.
        </Text>

        <Flex gap={2} justifyContent="flex-end" alignItems="center">
          <Button variant="secondary" onClick={onClose} borderRadius="small">
            Cancel
          </Button>

          <Button
            variant="primary"
            disabled={notifyMutation.isPending}
            type="submit"
            borderRadius="small"
            onClick={onConfirm}
          >
            {notifyMutation.isPending ? 'Sending...' : 'Notify Authors'}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
